import React, { useEffect, useState } from 'react'
import { Box, Card, Typography, Button, TextField, Paper, CircularProgress, CardMedia } from "@mui/material";
import { useSelector } from 'react-redux';
import { RootState } from 'src/interface/IRootState';
import { Bounce, ToastContainer } from 'react-toastify';
import errorNotify from 'src/notifications/errorNotify';
import { useNavigate, useParams } from 'react-router-dom';
import getStatus from 'src/function/getStatusOrder';
import style from '../styles/TransactionPage.module.css';
import { useWebSocket } from 'src/hooks/webSoket/useWebSocket';


const TransactionPage: React.FC = () => {
  const [isBuyer, setIsBuyer] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { socketId } = useParams();
  const userData = useSelector((state: RootState) => state.userData);
  const selectedLogos = useSelector((state: RootState) => state.images);
  const { orderData, isConnected, handleSendCoin, handleApprove, messages, sendMessage } = useWebSocket(socketId, userData.telegramId);

  useEffect(() => {
    if (!userData.telegramId) {
      navigate('/cabinet');
      return;
    }

    if (!orderData) {
      errorNotify('Ошибка подключения')
    }

    setIsBuyer(userData.telegramId === orderData?.buyerId);
  }, [navigate, orderData, userData.telegramId]);

  const handleClickLogo = () => {
    navigate('/cabinet');
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      sendMessage(message);
      setMessage('');
    }
  };

  if (!isConnected) {
    return (
      <div className={style.container}>
        <div className={style.header}>
          <div className={style.containerLogo} onClick={handleClickLogo}>
            <img src={selectedLogos.p2pLogo} alt='P2P Logo' style={{ cursor: 'pointer' }} />
          </div>
        </div>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>

        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Bounce}
        />
      </div>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div className={style.header}>
        <div className={style.containerLogo} onClick={handleClickLogo}>
          <img src={selectedLogos.p2pLogo} alt='P2P Logo' style={{ cursor: 'pointer' }} />
        </div>
      </div>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          p: 2,
          minHeight: { xs: "calc(100vh - 101px)", s: "calc(100vh - 81px)" },
          bgcolor: "#f4f4f4",
          boxSizing: "border-box",
        }}
      >
        <Card
          sx={{
            flexGrow: 1,
            p: 2,
            minHeight: "500px",
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Данные ордера
          </Typography>

          <CardMedia
            component="img"
            sx={{ maxHeight: 150, maxWidth: 210, objectFit: 'contain', my: 2 }}
            image={orderData.photo}
            alt="Order photo"
          />

          <Typography variant="body1" sx={{ mt: 1 }}>
            <strong>Имя:</strong> {orderData.name}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            <strong>Тип товара:</strong> {orderData.type}
          </Typography>
          <Typography variant="body1">
            <strong>Стоимость:</strong> {orderData.price} {orderData.currency.toUpperCase()}
          </Typography>
          <Typography variant="body1">
            <strong>Статус:</strong> {getStatus(orderData.statusOrder)}
          </Typography>
          <Typography variant="body1">
            <strong>Залог:</strong> {orderData.collateral.toFixed(4)} CASHBACK
          </Typography>
          <Typography variant="body1">
            <strong>Задаток:</strong> {orderData.downPayment.toFixed(4)} CASHBACK
          </Typography>
          <Typography variant="body1">
            <strong>Реквизиты для оплаты:</strong> {orderData.requisites}
          </Typography>
          <Typography variant="body1">
            <strong>Описание:</strong> {orderData.description}
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            {isBuyer
              ? (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSendCoin}
                  disabled={!orderData || orderData?.statusOrder !== 'WaitPayment'}
                  fullWidth>
                  Подтвердить оплату
                </Button>)
              : (<Button
                variant="contained"
                color="success"
                onClick={handleApprove}
                disabled={!orderData || orderData?.statusOrder !== 'PaidBuyer'}
                fullWidth>
                Подтвердить
              </Button>)}
          </Box>
        </Card>

        <Paper
          sx={{
            flexGrow: 1,
            p: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: "350px"
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Чат
          </Typography>
          <Box sx={{ flex: 1, overflowY: "auto", mt: 2, p: 1, bgcolor: "#fff", borderRadius: 1 }}>
            {messages.map((msg, index) => {
              const formattedDate = new Date(msg.timestamp).toLocaleString();

              return (
                <Typography key={index} variant="body2" sx={{ mt: 1 }}>
                  <strong>ID {msg.senderId}:</strong> {msg.message} ({formattedDate})
                </Typography>
              );
            })}
          </Box>
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Введіть повідомлення..."
              disabled={!isConnected || orderData?.statusOrder === 'Done'}
            />
            <Button 
              variant="contained" 
              color="primary" 
              disabled={!isConnected || orderData?.statusOrder === 'Done'}
              onClick={handleSendMessage}>
              Отправить
            </Button>
          </Box>
        </Paper>
      </Box>

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </Box>
  );
}

export default TransactionPage